import { onVisibilityChange } from '../helpers/DOM'
import Logger from '../helpers/Logger'
import { TimedFunction } from '../helpers/TimedFunction'
import { Slot } from '../slots/Slot'
import Refresh, { MINIMUM_VISIBILITY } from './Refresh'
import EventManager from '../eventManager/EventManager'
import { AdType } from '../enums/AdType'
import { removeStaleNativeAd } from '../native/helpers/removeStaleNativeAd'

export default class Refreshable {
  private _viewable = true
  private readyToRefresh: boolean
  private refreshFunction: TimedFunction
  private url: string
  private removeVisibilityListener: () => void

  constructor(readonly slot: Slot, private refresh: Refresh) {
    Logger.debug(
      `Creating Refreshable for slot`,
      slot,
      `in ${(slot.refreshTime as number) * 1000}`
    )

    this.url = window.location.href

    this.refreshFunction = new TimedFunction(
      () => this.onRefreshTimePassed(),
      slot.refreshTime
    )

    if (!slot.visible) {
      Logger.debug(`Pausing non-visible refreshable`, slot)
      this.refreshFunction.pause()
    }

    // onVisibilityChange returns a function that will removed the listeners it created when called.
    this.removeVisibilityListener = onVisibilityChange({
      onHidden: () => {
        Logger.debug(`Pausing refreshable:`, slot)
        this.refreshFunction.pause()
      },
      onVisible: () => {
        Logger.debug(`Restarting refreshable:`, slot)
        this.refreshFunction.start()
      }
    })
    EventManager.on(
      EventManager.events.slotVisibilityChanged,
      (visibility, slot) => {
        if (slot !== this.slot) {
          return
        }

        if (visibility >= MINIMUM_VISIBILITY) {
          Logger.debug('Restarting refreshable:', slot)
          this.refreshFunction.start()
        } else {
          Logger.debug('Pausing refreshable:', slot)
          this.refreshFunction.pause()
        }
      }
    )
  }

  set viewable(value: boolean) {
    if (value && this.readyToRefresh) {
      this.refreshSlot()
      this.resetRefreshable()
    }
  }

  private onRefreshTimePassed(): void {
    this.readyToRefresh = true
    if (this.slot.visible) {
      this.refreshSlot()
      this.resetRefreshable()
    }
  }

  private resetRefreshable(): void {
    this.removeVisibilityListener()
    this.refresh.registry[this.slot.id] = null
  }

  shortCircuit() {
    Logger.debug('Short Circuiting Refreshable: ', this)
    this.refreshFunction.shortCircuit()
  }

  private refreshSlot(): void {
    // If the URL has changed from what it was before for this refreshable (e.g. an SPA changed routes)
    // reset the slot.refreshes count.
    const currentUrl = window.location.href
    if (this.url !== currentUrl) {
      this.slot.refreshes = 1
    } else {
      this.slot.refreshes++
    }

    this.readyToRefresh = false

    if (this.slot.adType === AdType.inBannerNative) {
      removeTripleliftAd(this.slot)
    } else {
      removeStaleNativeAd(this.slot)
    }

    if (!this.slot.refreshSizeRestricted && this.slot.fixedHeight) {
      this.slot.refreshSizeRestricted = true
      this.slot.destroyGSlot()
      // if (this.slot.resizeObserver) {
      //   this.slot.resizeObserver.disconnect() // TODO: does this actually make sense? I'm not sure
      // }
    }
    EventManager.trigger(EventManager.events.slotRefreshed, this.slot)
    this.refresh.prebidFacade.requestBids([this.slot], this.slot.timeout.value)
  }
}

const removeTripleliftAd = (slot: Slot) => {
  const tlAd = slot.wrapper.querySelector('.tlod')
  tlAd?.remove()
  /* remove adunit styling */
  const adunit = slot.wrapper.querySelector('.adunit') as HTMLElement
  adunit.setAttribute('style', '')
}
